import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Platform, Pressable, Animated, Easing, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useRouter } from '@/lib/navigation';
import { authApi } from '@/lib/api';
import PropTypes from 'prop-types';
import { useModalStore, useToastStore, useAuthStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { RESPONSE_ACTION_TYPES, MESSAGE_REGENERATE_TYPES } from '@/constants/config';
import socketManager from '@/lib/socket';

// Create styles outside component
const createStyles = (colors) => StyleSheet.create({
  container: {
    marginTop: 8,
    marginBottom: 4,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.solid_color_secondary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  buttonPressed: {
    transform: [{ scale: 0.98 }],
  },
  buttonHovered: {
    backgroundColor: colors.solid_color_tertiary,
    transform: [{ scale: 1.015 }],
  },
  icon: {
    marginRight: 6,
  },
});

// Spinning Icon Component
const SpinningIcon = React.memo(({ style, color }) => {
  const spinValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    const spin = Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    );
    spin.start();
    return () => spin.stop();
  }, []);

  const spinAnimation = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <Animated.View style={[style, { transform: [{ rotate: spinAnimation }] }]}>
      <Icon
        name="spinner"
        size={14}
        color={color}
      />
    </Animated.View>
  );
});

SpinningIcon.propTypes = {
  style: PropTypes.object,
  color: PropTypes.string.isRequired,
};

SpinningIcon.displayName = 'SpinningIcon';

// Memoized button component
const MessageButton = React.memo(({ 
  button, 
  index, 
  onPress, 
  colors, 
  styles,
  isLoading,
}) => (
  <Pressable
    key={index}
    style={({ pressed, hovered }) => [
      styles.button,
      pressed && styles.buttonPressed,
      hovered && styles.buttonHovered,
    ]}
    onPress={() => onPress(button)}
    accessibilityRole="button"
    accessibilityLabel={button.title}
    accessibilityHint={button.hint || `Perform ${button?.title?.toLowerCase()} action`}
  >
    {button.icon && (
      isLoading ? (
        <SpinningIcon
          style={styles.icon}
          color={colors.text_color_primary}
        />
      ) : (
        <Icon
          name={button.icon}
          size={14}
          color={colors.text_color_primary}
          style={styles.icon}
          accessibilityLabel={`${button.icon} icon`}
        />
      )
    )}
    <Text 
      size='s'
      color='primary'
      accessibilityRole="text"
    >
      {button.title}
    </Text>
  </Pressable>
));

MessageButton.propTypes = {
  button: PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.string,
    hint: PropTypes.string,
    href: PropTypes.string,
    action: PropTypes.string,
    data: PropTypes.object,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onPress: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
};

MessageButton.displayName = 'MessageButton';

function MessageButtons({
  conversationId,
  messageId,
  buttons = [],
  onButtonClick,
}) {
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { addToast } = useToastStore(state => state.actions);
  const [loadingButtons, setLoadingButtons] = useState(new Set());
  const { t } = useTranslation();
  const userId = useAuthStore(state => state.user?.id);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isAnon = !userId;
  const requireAuth = useRequireAuth();
  
  // Memoize styles
  const styles = useMemo(() => createStyles(colors), [colors]);

  // Memoize button click handler
  const handleButtonClick = useCallback(async (button) => {
    try {
      // Set loading state for this button
      if (button.action === RESPONSE_ACTION_TYPES.VERIFY_EMAIL || 
          button.action === RESPONSE_ACTION_TYPES.RETRY) {
        setLoadingButtons(prev => new Set([...prev, button.title]));
      }

      // Handle direct navigation
      if (button.href) {
        router.push(button.href);
        return;
      }

      // Handle special actions
      switch (button.action) {
        case RESPONSE_ACTION_TYPES.MAKE_NEW_CONVERSATION:
          router.push('/app');
          break;

        case RESPONSE_ACTION_TYPES.MAKE_NEW_CUSTOM_AGENT:
          requireAuth(() => openModal('CustomAgentModal'), {
            source: 'message_button_make_new_custom_agent',
            title: 'Sign in to create a custom agent',
            subtitle: 'Custom agents are tied to your account so you can edit, share, and use them in chat.',
            icon: 'sparkles',
          });
          break;

        case RESPONSE_ACTION_TYPES.GHOST_SIGNUP:
          if (!isGhost) break;
          openModal('GhostSignupModal', {
            title: button?.data?.title,
            subtitle: button?.data?.subtitle,
          });
          break;

        case RESPONSE_ACTION_TYPES.LOGIN:
          if (Platform.OS === 'web' && !isXS) {
            openModal('LoginModal');
          } else {
            router.push('/login');
          }
          break;

        case RESPONSE_ACTION_TYPES.GO_PLUS:
          if (isPlus) break;
          requireAuth(
            () => openModal('PlusFeatureModal', { feature: button?.data?.feature || 'plus' }),
            {
              source: 'message_button_go_plus',
              title: 'Sign in to upgrade to Plus',
              subtitle: 'Plus is tied to your account so we can apply your subscription.',
              icon: 'crown',
            },
          );
          break;

        case RESPONSE_ACTION_TYPES.BUY_CREDITS:
          // Free users: model-gate paywall (buy credits OR Plus). Plus: every
          // way forward (Arya free / buy / Premium / refresh date).
          if (!isPlus) {
            openModal('ModelAccessPaywallModal', {
              title: button?.data?.title,
              subtitle: button?.data?.subtitle,
            });
            break;
          }
          openModal('OutOfCreditsModal', { source: 'message_button' });
          break;

        case RESPONSE_ACTION_TYPES.GET_CREDITS:
          // For free users show out-of-credits, Plus users choose credit pack first.
          // Anon visitors can't have credits — route to auth CTA.
          if (isAnon) {
            requireAuth(() => {}, {
              source: 'message_button_get_credits',
              title: 'Sign in to use credits',
              subtitle: 'Credits live on your account so we can apply them to image, video, and music generations.',
              icon: 'coins',
            });
            break;
          }
          if (isPlus) {
            openModal('OutOfCreditsModal', { source: 'message_button' });
          } else {
            openModal('OutOfCreditsModal', {
              title: button?.data?.title,
              subtitle: button?.data?.subtitle,
            });
          }
          break;

        case RESPONSE_ACTION_TYPES.VERIFY_EMAIL:
          await authApi.resendVerificationEmail();
          addToast(t('messages.buttons.verificationSent'), 'success');
          break;

        case RESPONSE_ACTION_TYPES.RETRY:
          if (!socketManager.isSocketConnected()) {
            console.warn('Socket not connected. Cannot regenerate message');
            return;
          }
          socketManager.emitRegenerateMessage({
            userId,
            conversationId: button?.data?.conversationId,
            messageId: button?.data?.messageId,
            regenerateType: MESSAGE_REGENERATE_TYPES.AGAIN,
          });
          break;          
          
        default:
          onButtonClick?.(button);
      }
    } catch (error) {
      console.error('Button action failed:', error);
      addToast(error.message || t('messages.buttons.actionFailed'), 'error');
    } finally {
      // Clear loading state for this button
      if (button.action === RESPONSE_ACTION_TYPES.VERIFY_EMAIL || 
          button.action === RESPONSE_ACTION_TYPES.RETRY) {
        setLoadingButtons(prev => {
          const next = new Set(prev);
          next.delete(button.title);
          return next;
        });
      }
    }
  }, [router, isXS, openModal, addToast, onButtonClick, isGhost, isPlus]);

  if (!buttons?.length) return null;

  // Plus: hide go-plus. Free: if buy-credits is present (model gate), hide go-plus
  // so we show one unlock CTA that opens ModelAccessPaywallModal.
  const hasBuyCredits = buttons.some((b) => b?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS);
  const validButtons = buttons.filter((button) => {
    if (!button.title || button.title === 'undefined' || button.title === 'null' || button.title.trim() === '') {
      return false;
    }
    if (isPlus && button.action === RESPONSE_ACTION_TYPES.GO_PLUS) return false;
    if (!isPlus && button.action === RESPONSE_ACTION_TYPES.GO_PLUS && hasBuyCredits) return false;
    return true;
  });

  if (!validButtons.length) return null;

  return (
    <View 
      style={styles.container}
      accessibilityRole="toolbar"
      accessibilityLabel="Message action buttons"
    >
      {validButtons.map((button, index) => (
        <MessageButton
          key={index}
          button={button}
          index={index}
          onPress={handleButtonClick}
          colors={colors}
          styles={styles}
          isLoading={loadingButtons.has(button.title)}
        />
      ))}
    </View>
  );
}

MessageButtons.propTypes = {
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon: PropTypes.string,
    hint: PropTypes.string,
    href: PropTypes.string,
    action: PropTypes.string,
    data: PropTypes.object,
  })),
  onButtonClick: PropTypes.func,
};

MessageButtons.displayName = 'MessageButtons';

export default React.memo(MessageButtons); 